import { useMemo } from 'react'

import { NO_DATA_COLOR, SURFACE_COLOR } from './palette'
import {
  resolveLightColor,
  useIsSnapshotChartTheme,
} from './snapshotChartTheme'
import { MetricUnit, SummaryMode } from './types'
import type { ChartSeries } from './types'
import { useDocumentColorScheme } from './useDocumentColorScheme'

/**
 * Shared scaffolding for the Cartesian, time-axis charts (TimeSeriesChart +
 * ColumnChart): constants, colour resolution, base row building, and domain
 * maths. JSX-free — each chart composes these with its own axes + marks.
 */

// ── Layout constants ──────────────────────────────────────────────────────────
export const ONE_DAY_MS = 86_400_000
export const Y_TICK_COUNT = 5
export const TICK_FONT_SIZE = 12
export const X_AXIS_PADDING = 20
/** Fixed vertical gap (px) between adjacent horizontal grid lines / y-axis ticks.
 *  The plot area is locked to a multiple of this so every gridline sits an exact
 *  48px below the last, whatever the data range (see {@link fitToGrid}). */
export const GRID_GAP_PX = 48
/** Recharts' default chart margin, spelled out so the plot-area height is fully
 *  deterministic — equal to the previous (implicit) default, so no visual shift
 *  beyond the intended 48px rounding. */
export const CHART_MARGIN = { top: 5, right: 5, bottom: 5, left: 5 }
/** Recharts' default `<XAxis>` height, made explicit for the same reason. */
export const DEFAULT_X_AXIS_HEIGHT = 30
/** Minimum horizontal gap (px) recharts must keep between adjacent x-axis date
 *  labels; below this it drops the overlapping label. Lets narrow (mobile) axes
 *  thin instead of squishing, while roomy axes still show every tick. */
export const X_AXIS_MIN_TICK_GAP = 16

// Y-axis band-width sizing, shared with the old MetricChart primitives so the
// two chart systems never drift. @see ./yAxisWidth
export {
  axisTickLabels,
  computeYAxisWidth,
  Y_AXIS_LABEL_GAP,
  Y_AXIS_LABEL_OFFSET,
  Y_AXIS_TICK_MARGIN,
  Y_AXIS_WIDTH,
} from './yAxisWidth'

// ── Domain padding ──────────────────────────────────────────────────────────
const PERCENTAGE_RANGE_PAD = 0.1
const PERCENTAGE_FLAT_PAD_MIN = 0.5
const COUNT_RANGE_PAD = 0.04
const COUNT_FLAT_PAD = 0.05
const COUNT_FLAT_PAD_MIN = 1

/** One chart row: a timestamp plus a value (or null) per series id. */
export interface ChartDataRow {
  timestamp: number
  [key: string]: number | string | null
}

/** Resolve a `var(--…)` token to its computed value (recharts + the export
 *  snapshot need concrete colours); pass through literal colours. */
export function resolveColor(cssVar: string): string {
  if (!cssVar.startsWith('var(')) return cssVar
  const propName = cssVar.slice(4, -1).trim()
  return getComputedStyle(document.documentElement)
    .getPropertyValue(propName)
    .trim()
}

/** UTC midnight for a date, matching how API points are stored (00:00:00Z). */
export const toUtcMidnight = (d: Date): number =>
  Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())

/** Base metric of a series — strips the `-comparison` suffix so a primary and
 *  its comparison sibling group together (and drive one axis). */
export const baseKey = (s: ChartSeries): string =>
  s.id.replace(/-comparison$/, '')

export function makeFormatDateTick(
  startDate?: Date,
  endDate?: Date,
  monthly = false,
): (timestamp: number) => string {
  const spansYears =
    startDate != null &&
    endDate != null &&
    startDate.getFullYear() !== endDate.getFullYear()
  return (timestamp) =>
    new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      ...(monthly ? {} : { day: 'numeric' }),
      ...(spansYears ? { year: 'numeric' } : {}),
      timeZone: 'UTC',
    })
}

export interface SeriesRows {
  data: ChartDataRow[]
  timestamps: number[]
  /** Maps a primary-axis timestamp to a representative comparison original date,
   *  for the stacked second X-axis label (line charts only). */
  comparisonDateByTimestamp: Map<number, string>
}

/**
 * Projects every series onto the union of their timestamps: one row per day,
 * one value per series id. A point whose `source` is `missing` or `computed` is
 * a genuine gap and maps to `null` regardless of summaryMode, matching the
 * tooltip's {@link classifyPoint}; otherwise a per-day Total maps `value ?? 0`
 * (to match the bars) and any other series maps `value ?? null`. Greying (the
 * split into observed + grey lines) is layered on top by TimeSeriesChart;
 * columns use these rows as-is.
 */
export function buildSeriesRows(series: ChartSeries[]): SeriesRows {
  const timestampSet = new Set<number>()
  for (const s of series) {
    for (const point of s.data) timestampSet.add(new Date(point.date).getTime())
  }
  const timestamps = Array.from(timestampSet).toSorted((a, b) => a - b)

  const comparisonDateByTimestamp = new Map<number, string>()
  const seriesLookups = series.map((s) => {
    const lookup = new Map<number, number | null>()
    for (const point of s.data) {
      const ts = new Date(point.date).getTime()
      const isGap = point.source === 'missing' || point.source === 'computed'
      lookup.set(
        ts,
        isGap
          ? null
          : s.summaryMode === SummaryMode.TOTAL
            ? (point.value ?? 0)
            : (point.value ?? null),
      )
      if (point.originalDate && !comparisonDateByTimestamp.has(ts)) {
        comparisonDateByTimestamp.set(ts, point.originalDate)
      }
    }
    return { key: s.id, lookup }
  })

  const data: ChartDataRow[] = timestamps.map((ts) => {
    const row: ChartDataRow = { timestamp: ts }
    for (const { key, lookup } of seriesLookups)
      row[key] = lookup.get(ts) ?? null
    return row
  })

  return { data, timestamps, comparisonDateByTimestamp }
}

/** Numeric time X-domain: the given range (UTC midnight), else the data extent. */
export function computeXDomain(
  timestamps: number[],
  startDate?: Date,
  endDate?: Date,
): [number, number] {
  const first = timestamps[0]
  const last = timestamps[timestamps.length - 1]
  if (startDate && endDate)
    return [toUtcMidnight(startDate), toUtcMidnight(endDate)]
  if (first == null || last == null) return [-ONE_DAY_MS, ONE_DAY_MS]
  if (first === last) return [first - ONE_DAY_MS, last + ONE_DAY_MS]
  return [first, last]
}

/**
 * Snap a date (ms) to the plotted bucket whose period *contains* it — the greatest
 * bucket start at or before `ts`. Buckets on a category axis are period starts, so
 * nearest-by-distance would round a mid-period date into the next bucket (a
 * mid-March date onto the April bar); a March event must mark the March bucket.
 * Falls back to the earliest bucket when the date precedes all of them (e.g. data
 * collection began before the visible range). `bucketTimestamps` need not be sorted.
 */
export function snapToBucket(ts: number, bucketTimestamps: number[]): number {
  if (bucketTimestamps.length === 0) return ts
  let earliest = bucketTimestamps[0]
  let containing: number | null = null
  for (const t of bucketTimestamps) {
    if (t < earliest) earliest = t
    if (t <= ts && (containing == null || t > containing)) containing = t
  }
  return containing ?? earliest
}

/**
 * Locks the plot area to a whole number of {@link GRID_GAP_PX} gaps that fit the
 * given height, returning the adjusted chart height (plot + chrome) and the tick
 * count that produces one gridline per 48px. `xAxisHeight` is the axis band's real
 * height (default / stacked / annotation) so the maths matches what recharts draws.
 */
export function fitToGrid(
  height: number,
  xAxisHeight: number,
): { chartHeight: number; tickCount: number } {
  const chromeV = CHART_MARGIN.top + CHART_MARGIN.bottom + xAxisHeight
  const gaps = Math.max(1, Math.round((height - chromeV) / GRID_GAP_PX))
  return { chartHeight: gaps * GRID_GAP_PX + chromeV, tickCount: gaps + 1 }
}

/** Smallest "nice" tick step (1 / 2 / 2.5 / 5 × 10ⁿ) at or above `raw`. The 2.5
 *  rung keeps the axis from over-shooting the data on a fixed tick count — e.g.
 *  a max of 87 over 4 gaps needs a step ≥ 21.75, which 1/2/5 rounds up to 50
 *  (top 200) but 2.5 lands on 25 (top 100). On a count axis (`integer`) the step
 *  is a whole number so ticks stay integer. */
export function niceStep(raw: number, integer: boolean): number {
  if (!(raw > 0)) return integer ? 1 : raw
  const pow = 10 ** Math.floor(Math.log10(raw))
  const norm = raw / pow // [1, 10)
  const niceNorm =
    norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10
  const step = niceNorm * pow
  return integer ? Math.max(1, Math.round(step)) : step
}

/**
 * Y-axis domain for one axis' series. Columns run `[0, top]` where `top` is the
 * next tick step above the data max — so the tallest column always sits a step
 * below the top gridline rather than flush to it (stacked sums when stacked).
 * Lines pad around the min/max so they fill the plot vertically.
 */
export function computeYDomain(
  data: ChartDataRow[],
  seriesForAxis: ChartSeries[],
  opts: { column: boolean; stacked: boolean; empty: boolean },
): [number, number | string] {
  if (opts.empty) return [0, 5]
  if (data.length === 0 || seriesForAxis.length === 0) return [0, 'auto']

  let min = Infinity
  let max = 0
  for (const row of data) {
    if (opts.stacked) {
      let sum = 0
      for (const s of seriesForAxis) {
        const val = row[s.id]
        if (typeof val === 'number') sum += val
      }
      if (sum > 0) {
        max = Math.max(max, sum)
        min = Math.min(min, sum)
      }
    } else {
      for (const s of seriesForAxis) {
        const val = row[s.id]
        if (typeof val === 'number') {
          max = Math.max(max, val)
          min = Math.min(min, val)
        }
      }
    }
  }

  if (max === 0) return [0, 'auto']
  if (opts.column) {
    // Headroom: round the top up to the next tick step above the data max, so a
    // column whose value equals the max isn't drawn flush against the top edge.
    const isPercentageAxis = seriesForAxis[0]?.unit === MetricUnit.PERCENTAGE
    const step = niceStep(max / (Y_TICK_COUNT - 1), !isPercentageAxis)
    const flushTop = Math.ceil(max / step) * step
    return [0, flushTop > max ? flushTop : flushTop + step]
  }

  const range = max - min
  const isPercentageAxis = seriesForAxis[0]?.unit === MetricUnit.PERCENTAGE
  if (isPercentageAxis) {
    const padding =
      range > 0
        ? range * PERCENTAGE_RANGE_PAD
        : Math.max(max * PERCENTAGE_RANGE_PAD, PERCENTAGE_FLAT_PAD_MIN)
    return [Math.max(0, min - padding), max + padding]
  }
  const padding =
    range > 0
      ? range * COUNT_RANGE_PAD
      : Math.max(max * COUNT_FLAT_PAD, COUNT_FLAT_PAD_MIN)
  return [Math.max(0, Math.floor(min - padding)), Math.ceil(max + padding)]
}

export interface ChartChrome {
  /** Resolved line/bar colour per series id. */
  resolvedColors: Record<string, string>
  /** Resolved grey for the interpolated no-data line segments + hollow markers. */
  noDataColor: string
  /** Resolved chart-surface fill for hollow no-data markers (open rings). */
  surfaceColor: string
  gridColor: string
  neutralTextColor: string
  subtleTextColor: string
  cursor: { stroke: string }
}

/**
 * Resolves the CSS-var colours (series + grid/axis/cursor + no-data grey + surface)
 * to concrete values, re-resolving when the document theme flips. Charts read
 * colour from here (and, upstream, from {@link ChartSeriesContext}) — never props.
 */
export function useChartChrome(series: ChartSeries[]): ChartChrome {
  const colorScheme = useDocumentColorScheme()
  // The offscreen Share-as-Post snapshot renders on a fixed light card, so it
  // resolves the light-theme palette regardless of the app's current theme,
  // and renders both axes' tick labels in the subtle text token (design:
  // dates + amounts both use text/subtle). @see snapshotChartTheme
  const isSnapshot = useIsSnapshotChartTheme()
  const resolve = isSnapshot ? resolveLightColor : resolveColor
  return useMemo(
    () => {
      const resolvedColors: Record<string, string> = {}
      for (const s of series) resolvedColors[s.id] = resolve(s.color)
      const gridColor = resolve('var(--color-border-subtle)')
      const subtleTextColor = resolve('var(--color-text-subtle)')
      return {
        resolvedColors,
        noDataColor: resolve(NO_DATA_COLOR),
        surfaceColor: resolve(SURFACE_COLOR),
        gridColor,
        neutralTextColor: isSnapshot
          ? subtleTextColor
          : resolve('var(--color-text-neutral)'),
        subtleTextColor,
        cursor: { stroke: gridColor },
      }
    },
    // colorScheme re-resolves the CSS tokens on theme flip; isSnapshot switches
    // the resolver + axis-text token for the offscreen snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, colorScheme, isSnapshot],
  )
}
